import React from 'react';
import PropTypes from "prop-types";
import Typography from "@mui/material/Typography";
import { withStyles } from "@mui/styles";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import { toast } from "react-toastify";
import FormControlLabel from '@mui/material/FormControlLabel';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from "@mui/material/Checkbox";
import TextareaAutosize from 'react-autosize-textarea';
import DialogContentText from "@mui/material/DialogContentText";
import { withTranslation } from 'react-i18next';


const styles = theme => ({
  root: {
    width: "100%"
  },
  paper: {
    borderRadius: 11,
    boxShadow: theme.shadows[6],
    overflow: 'hidden',
    borderTop: '3px solid #0a5d66',
  },
  paddedBoxDivEdit: {
    padding: theme.spacing(2.25, 2.5, 0),
    minWidth: 520,
  },
  dialogTitle: {
    fontSize: '1.2rem',
    fontWeight: 600,
    color: 'rgba(0, 0, 0, 0.87)',
    paddingBottom: theme.spacing(1.75),
    marginBottom: 0,
    borderBottom: '2px solid rgba(10, 93, 102, 0.2)',
  },
  titleAccent: {
    color: '#0a5d66',
    fontWeight: 700,
    fontSize: '1.45rem',
    marginLeft: theme.spacing(1),
  },
  sectionCard: {
    backgroundColor: 'rgba(10, 93, 102, 0.05)',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1.5),
    border: '1px solid rgba(10, 93, 102, 0.15)',
  },
  sectionCardLast: {
    marginBottom: theme.spacing(1.25),
    backgroundColor: 'rgba(10, 93, 102, 0.03)',
    borderColor: 'rgba(10, 93, 102, 0.12)',
  },
  sectionTitle: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#0a5d66',
    marginBottom: theme.spacing(1),
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  sectionLabel: {
    color: 'rgba(0, 0, 0, 0.87)',
    fontWeight: 500,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(0.5),
    display: 'block',
  },
  notesSection: {
    minHeight: 0,
  },
  tableWrap: {
    overflowX: 'auto',
    borderRadius: 8,
    border: '1px solid rgba(10, 93, 102, 0.2)',
    backgroundColor: '#fff',
  },
  emptyTableHint: {
    fontSize: '0.8125rem',
    color: 'rgba(0, 0, 0, 0.45)',
    fontStyle: 'italic',
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  releaseRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 0),
  },
  notesTextarea: {
    minHeight: 120,
    maxHeight: 180,
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid rgba(10, 93, 102, 0.3)',
    borderRadius: 8,
    fontSize: '0.875rem',
    padding: theme.spacing(1.25, 1.5),
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    color: 'rgba(0, 0, 0, 0.87)',
    resize: 'vertical',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    backgroundColor: '#fff',
    '&:focus': {
      outline: 'none',
      borderColor: '#0a5d66',
      boxShadow: '0 0 0 2px rgba(10, 93, 102, 0.25)',
    },
  },
  contentRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    marginTop: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  contentCol: {
    flex: 1,
    minWidth: 0,
  },
  table: {
    minWidth: 230,
    '& th, & td': {
      borderBottom: '1px solid rgba(10, 93, 102, 0.1)',
    },
    '& tbody tr:hover': {
      backgroundColor: 'rgba(10, 93, 102, 0.08)',
    },
    '& tbody tr:nth-of-type(even)': {
      backgroundColor: 'rgba(10, 93, 102, 0.04)',
    },
    '& tbody tr:nth-of-type(even):hover': {
      backgroundColor: 'rgba(10, 93, 102, 0.1)',
    },
  },
  TableHead: {
    backgroundColor: 'rgba(10, 93, 102, 0.12)',
  },
  tableHeaderCell: {
    padding: theme.spacing(1, 1.25),
    fontSize: '0.72rem',
    fontWeight: 700,
    color: '#0a5d66',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  NameCellWidth: {
    minWidth: 76,
  },
  AmountCell: {
    minWidth: 60,
    textAlign: 'right',
  },
  removePadding: {},
  AmountAlignment: {
    textAlign: 'right',
  },
  tableBodyCell: {
    padding: theme.spacing(1, 1.25),
    fontSize: '0.875rem',
    color: 'rgba(0, 0, 0, 0.87)',
  },
  releaseSection: {
    marginTop: 0,
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(0.75),
    borderTop: '1px solid rgba(10, 93, 102, 0.15)',
  },
  dialogActions: {
    padding: theme.spacing(1.75, 2.5),
    gap: theme.spacing(1.75),
    borderTop: '1px solid rgba(10, 93, 102, 0.15)',
    backgroundColor: 'rgba(10, 93, 102, 0.06)',
    justifyContent: 'space-between',
  },
  primaryButton: {
    background: 'linear-gradient(180deg, #0a8f7a 0%, #0a5d66 100%)',
    color: '#fff',
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(0.875, 2.25),
    borderRadius: 8,
    minWidth: 130,
    fontSize: '0.875rem',
    transition: 'opacity 0.2s ease, transform 0.1s ease',
    boxShadow: '0 1px 3px rgba(10, 93, 102, 0.3)',
    '&:hover': {
      opacity: 0.95,
      color: '#fff',
      boxShadow: '0 2px 6px rgba(10, 93, 102, 0.4)',
    },
    '&:disabled': {
      color: 'rgba(0, 0, 0, 0.38)',
      cursor: 'not-allowed',
      background: '#E4E5E8',
      boxShadow: 'none',
    },
  },
  secondaryButton: {
    color: '#0a5d66',
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(0.875, 2),
    borderRadius: 8,
    minWidth: 90,
    fontSize: '0.875rem',
    border: '1px solid rgba(10, 93, 102, 0.4)',
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(10, 93, 102, 0.08)',
      borderColor: '#0a5d66',
    },
  },
  flex: {
    color: 'rgba(0, 0, 0, 0.87)',
    fontWeight: 500,
  },
  // Confirm / release dialogs
  confirmPaper: {
    borderRadius: 11,
    boxShadow: theme.shadows[6],
    borderTop: '3px solid #0a5d66',
  },
  DialogContentWrap: {
    padding: theme.spacing(2.5),
    textAlign: 'center',
  },
  DialogText: {
    fontSize: '0.875rem',
    color: 'rgba(0, 0, 0, 0.75)',
    lineHeight: 1.5,
  },
  ButtonAlign: {
    padding: theme.spacing(2, 2.5),
    justifyContent: 'center',
    gap: theme.spacing(1.75),
  },
  DialogBack: {
    borderRadius: 10,
    padding: theme.spacing(1.25),
    background: 'linear-gradient(155deg, rgba(10, 93, 102, 0.08) 0%, rgba(10, 93, 102, 0.04) 90%)',
  },
  buttonFace: {
    textTransform: 'none',
    fontWeight: 600,
  },
  waitIcon: {
    position: 'absolute',
    top: '40%',
    left: '40%',
    zIndex: '99999999'
  },
  imgProperty: {
    height: '100px'
  }
});
class Manualconsultation extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      DialogOpen: this.props.detailDialogOpen,
      isButtonDisabled: false,
      PatietPrescriptionData: '',
      consultnotes: '',
      ReleaseOrder: false,
      ReleaseOrderOpen: false,
      orderNotReleasedOpen: false,
      UserId: '',
      UserName: '',
    }
  }
  componentWillMount() {
    this.getPrescriptionDetailByPickupCode(this.props.pickupCode);
    this.getManualReleaseOrderDetailByPickupCode(this.props.pickupCode);
    fetch(process.env.REACT_APP_BASEURL + 'users/' + JSON.parse(sessionStorage.getItem('user')).username, {
      method: 'GET',
      withCredentials: true,
      headers: {
        'Authorization': 'Bearer ' + JSON.parse(sessionStorage.getItem('user')).signInUserSession.idToken.jwtToken,
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        try {
          return response.text();
        } catch (error) {
          return null;
        }
      })
      .then(uData => {
        if (uData) {
          const userData = JSON.parse(uData);
          this.setState({ UserId: userData[0].id, UserName: userData[0].name })
        }
      });
  }
  handleClose = () => {
    this.setState({ DialogOpen: false, orderNotReleasedOpen: false });
    this.props.onPopupClose();
  }
  getPrescriptionDetailByPickupCode = (pickupcode) => {
    fetch(process.env.REACT_APP_BASEURL + 'getprescriptiondetailbypucode/' + pickupcode, {
      method: 'GET',
      withCredentials: true,
      headers: {
        'Authorization': 'Bearer ' + JSON.parse(sessionStorage.getItem('user')).signInUserSession.idToken.jwtToken,
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        try {
          return response.text();
        } catch (error) {
          return null;
        }
      })
      .then(pData => {
        if (pData) {
          let data = JSON.parse(pData);
          this.setState({ PatietPrescriptionData: data })
        }
      });
  }
  createRow = () => {
    const { classes } = this.props;
    let Data = this.state && this.state.PatietPrescriptionData;
    if (Data) {
      return (Data.map((val, idx) => {
        return (<TableRow key={idx}>
          <TableCell className={`${classes.NameCellWidth} ${classes.tableBodyCell}`}>
            {val.rx_no}
          </TableCell>
          <TableCell className={`${classes.NameCellWidth} ${classes.tableBodyCell}`}>
            {val.drug_name}
          </TableCell>
          <TableCell className={`${classes.AmountCell} ${classes.tableBodyCell}`}>
            {val.doseage_form}
          </TableCell>
          <TableCell className={`${classes.AmountCell} ${classes.tableBodyCell}`}>
            {val.qty}
          </TableCell>
        </TableRow>)
      }))
    }
  }
  handleChangeConsultNotes = event => {
    event.preventDefault && event.preventDefault();
    this.setState({ consultnotes: event.target.value });
  };
  handleReleaseOrder = () => {
    this.setState({ ReleaseOrderOpen: true });
  };
  releaseorderaccept = () => {
    this.setState({ ReleaseOrder: !this.state.ReleaseOrder });
    var statusObj = {
      consult_start_date: new Date(),
      consult_end_date: new Date(),
      pickup_code: this.props.pickupCode,
      user_name: this.props.selectedObject.FirstName + this.props.selectedObject.LastName,
      call_duration: '00:00:00',
      user_id: this.state.UserId,
      consultation_name: this.state.UserName,
      Status: 'Offline',
      accounts_id: this.props.accountDetails.id,
      store_name: this.props.accountDetails.name,
      box_id: this.props.boxId,
      kiosk_name: this.props.pharmacyDetails.name,
      consultation_notes: this.state.consultnotes,
      is_releaseorder: true
    }
    fetch(process.env.REACT_APP_BASEURL + 'saveuserconsult', {
      method: 'post',
      withCredentials: true,
      headers: {
        'Authorization': 'Bearer ' + JSON.parse(sessionStorage.getItem('user')).signInUserSession.idToken.jwtToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(statusObj)
    })
      .then(response => {
        try {
          return response.text();
        } catch (error) {
          return null;
        }
      })
      .then(uData => {
        this.state.orderNotReleasedOpen ? this.savemanualuserConsult() : this.setState({ ReleaseOrderOpen: false });
        toast.success(this.props.t('Toast.Success.OrderReleased'));
        window.socket &&
          window.socket.emit &&
          window.socket.emit('isOrderReleasedFromDeliveryPrescription', this.props.pickupCode);
      });
  };
  handleCloseRleaseOrder = () => {
    this.setState({ ReleaseOrderOpen: false });
  };
  handleConsultClose = () => {
    this.setState({ orderNotReleasedOpen: false });
  };
  savemanualuserConsult = () => {
    let isConsultRequired = false;
    this.props
      && this.props.searchedRxItems
      && this.props.searchedRxItems.length > 0
      && this.props.searchedRxItems.map((i) => {
        if (i.is_consultrequire === true) {
          isConsultRequired = true;
        }
        return false;
      });
    var statusObj = {
      consult_start_date: new Date(),
      consult_end_date: new Date(),
      pickup_code: this.props.pickupCode,
      user_name: this.props.selectedObject.FirstName + this.props.selectedObject.LastName,
      call_duration: '00:00:00',
      user_id: this.state.UserId,
      consultation_name: this.state.UserName,
      Status: 'Offline',
      accounts_id: this.props.accountDetails.id,
      store_name: this.props.accountDetails.name,
      box_id: this.props.boxId,
      kiosk_name: this.props.pharmacyDetails.name,
      consultation_notes: this.state.consultnotes,
      is_releaseorder: this.state.ReleaseOrder,
      isConsultRequired,
    }
    fetch(process.env.REACT_APP_BASEURL + 'saveuserconsult', {
      method: 'post',
      withCredentials: true,
      headers: {
        'Authorization': 'Bearer ' + JSON.parse(sessionStorage.getItem('user')).signInUserSession.idToken.jwtToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(statusObj)
    })
      .then(response => {
        try {
          return response.text();
        } catch (error) {
          return null;
        }
      })
      .then(uData => {
        toast.success(this.props.t('Toast.Success.ConsultComplete'));
        this.handleClose();
      });
  };
  getManualReleaseOrderDetailByPickupCode = (pickupcode) => {
    fetch(process.env.REACT_APP_BASEURL + 'getmanualreleaseorderdetailbypickupcode/' + pickupcode, {
      method: 'GET',
      withCredentials: true,
      headers: {
        'Authorization': 'Bearer ' + JSON.parse(sessionStorage.getItem('user')).signInUserSession.idToken.jwtToken,
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        try {
          return response.text();
        } catch (error) {
          return null;
        }
      })
      .then(pData => {
        if (pData) {
          let data = JSON.parse(pData);
          let item = Array.isArray(data) ? data[0] : data;
          if (item) {
            this.setState({ ReleaseOrder: item.is_releaseorder });
            this.setState({ consultnotes: item.consultation_notes });
          }
        }
      });
  }
  render() {
    const { classes, t } = this.props;
    return (
      <div className={classes.root}>
        <Dialog
          open={this.state.DialogOpen || false}
          aria-labelledby="consult-dialog-title"
          aria-describedby="consult-dialog-description"
          fullWidth
          maxWidth="md"
          PaperProps={{ className: classes.paper }}
        >
          <div className={classes.paddedBoxDivEdit}>
            <DialogTitle id="consult-dialog-title" className={classes.dialogTitle} disableTypography>
              <Typography component="span" style={{ fontSize: '1.2rem', fontWeight: 600 }}>
                {t("Assign.ManualConsult.ConsultRoom")}
              </Typography>
              <span className={classes.titleAccent}>{this.props.pickupCode}</span>
            </DialogTitle>

            <div className={`${classes.sectionCard} ${classes.notesSection}`}>
              <Typography variant="subtitle2" className={classes.sectionTitle}>
                {t("Assign.ManualConsult.ConsultNotes")}
              </Typography>
              <TextareaAutosize
                className={classes.notesTextarea}
                onBlur={this.handleChangeConsultNotes}
                onChange={this.handleChangeConsultNotes}
                defaultValue={this.state.consultnotes}
                value={this.state.consultnotes}
                placeholder={t("Assign.ManualConsult.ConsultNotes")}
              />
            </div>

            <div className={`${classes.sectionCard} ${classes.sectionCardLast}`}>
              <Typography variant="subtitle2" className={classes.sectionTitle}>
                {t("Assign.ManualConsult.PrescriptionItems")}
              </Typography>
              <div className={classes.tableWrap}>
                {this.state.PatietPrescriptionData && this.state.PatietPrescriptionData.length > 0 ? (
                  <Table className={classes.table} size="small">
                    <TableHead className={classes.TableHead}>
                      <TableRow>
                        <TableCell className={`${classes.NameCellWidth} ${classes.tableHeaderCell}`}>
                          {t("Assign.ManualConsult.RxHash")}
                        </TableCell>
                        <TableCell className={`${classes.NameCellWidth} ${classes.tableHeaderCell}`}>
                          {t("Assign.ManualConsult.DrugName")}
                        </TableCell>
                        <TableCell className={`${classes.AmountCell} ${classes.tableHeaderCell}`}>
                          {t("Assign.ManualConsult.Dose")}
                        </TableCell>
                        <TableCell className={`${classes.AmountCell} ${classes.tableHeaderCell}`}>
                          {t("Assign.ManualConsult.Qty")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {this.createRow()}
                    </TableBody>
                  </Table>
                ) : (
                  <div className={classes.emptyTableHint}>
                    {t("Assign.ManualConsult.NoItemsToDisplay")}
                  </div>
                )}
              </div>
            </div>

            <div className={classes.releaseSection}>
              <div className={classes.releaseRow}>
                <div className={classes.releaseLabelWrap}>
                  <FormControlLabel
                    label={t("Assign.ManualConsult.ReleaseOrder")}
                    control={
                      <Checkbox
                        disabled={this.state.ReleaseOrder}
                        checked={this.state.ReleaseOrder}
                        onChange={() => this.handleReleaseOrder('')}
                        value={this.state.ReleaseOrder}
                        color="primary"
                        size="small"
                      />
                    }
                    style={{ marginLeft: 0, marginRight: 0 }}
                  />
                </div>
              </div>
            </div>

            <DialogActions className={classes.dialogActions}>
              <Button
                onClick={this.handleClose}
                className={classes.secondaryButton}>
                {t("Assign.ManualConsult.Close")}
              </Button>
              <Button
                type="submit"
                disabled={this.state.isButtonDisabled}
                className={classes.primaryButton}
                variant="contained"
                onClick={
                  this.state.ReleaseOrder ? this.savemanualuserConsult : () => this.setState({ orderNotReleasedOpen: true })}>
                {t("Assign.ManualConsult.CompleteConsult")}
              </Button>
            </DialogActions>
          </div>
        </Dialog>
        <Dialog
          open={this.state.ReleaseOrderOpen}
          onClose={this.handleCloseRleaseOrder}
          aria-labelledby="release-confirm-title"
          aria-describedby="release-confirm-description"
          PaperProps={{ className: classes.confirmPaper }}
        >
          <div className={classes.DialogBack}>
            <DialogContent className={classes.DialogContentWrap}>
              <DialogContentText id="release-confirm-description" className={classes.DialogText}>
                {t("Assign.ManualConsult.AreYouSureToReleaseOrder")}
              </DialogContentText>
            </DialogContent>
            <DialogActions className={classes.ButtonAlign}>
              <Button
                className={classes.primaryButton}
                variant="contained"
                onClick={this.releaseorderaccept}>
                {t("Assign.ManualConsult.Yes")}
              </Button>
              <Button
                disabled={this.state.isButtonDisabled}
                className={classes.secondaryButton}
                onClick={this.handleCloseRleaseOrder}>
                {t("Assign.ManualConsult.No")}
              </Button>
            </DialogActions>
          </div>
        </Dialog>
        <Dialog
          open={this.state.orderNotReleasedOpen}
          onClose={this.handleConsultClose}
          aria-labelledby="order-not-released-title"
          aria-describedby="order-not-released-description"
          PaperProps={{ className: classes.confirmPaper }}
        >
          <DialogContent className={classes.DialogContentWrap}>
            <DialogContentText id="order-not-released-description" className={classes.DialogText}>
              {t("Assign.ManualConsult.ConfirmToReleaseOrder")}
            </DialogContentText>
          </DialogContent>
          <DialogActions className={classes.ButtonAlign}>
            <Button
              className={classes.primaryButton}
              variant="contained"
              onClick={this.releaseorderaccept}>
              {t("Assign.ManualConsult.ReleaseOrder")}
            </Button>
            <Button
              disabled={this.state.isButtonDisabled}
              className={classes.primaryButton}
              variant="contained"
              onClick={this.savemanualuserConsult}>
              {t("Assign.ManualConsult.CompleteConsult")}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}
Manualconsultation.propTypes = {
  classes: PropTypes.object.isRequired
};
export default withStyles(styles, { withTheme: true })(withTranslation()(Manualconsultation));



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/Manual-consultation.js